import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Building2, 
  Check, 
  ArrowRight, 
  Shield, 
  Zap, 
  BarChart3, 
  Bell,
  MessageSquare,
  Database,
  Download,
  Search,
  MapPin,
  Loader2,
  Lock,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const plans = [
  {
    id: 'individual',
    name: 'Individual',
    description: 'For individual property managers',
    pricing: [
      { term: '1 Year', price: 99, planId: 'individual_1yr' },
      { term: '2 Year', price: 79, planId: 'individual_2yr' },
      { term: '3 Year', price: 49, planId: 'individual_3yr' },
    ],
    datasets: ['DOB Violations'],
    features: [
      'Up to 25 properties',
      'Basic search & filters',
      'Email alerts',
      'AI chat (50 queries/mo)',
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'For growing portfolios',
    pricing: [
      { term: '1 Year', price: 149, planId: 'pro_yearly' },
    ],
    datasets: ['DOB Violations', 'Fire Dept', 'ECB'],
    features: [
      'Up to 100 properties',
      'Advanced filters & exports',
      'Priority email alerts',
      'AI chat (unlimited)',
      'Report templates',
      'CRM integration'
    ],
    featured: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For large organizations',
    pricing: [
      { term: '1 Year', price: 699, planId: 'enterprise_1yr' },
      { term: '2 Year', price: 599, planId: 'enterprise_2yr' },
      { term: '3 Year', price: 499, planId: 'enterprise_3yr' },
    ],
    datasets: ['All 15+ datasets included'],
    features: [
      'Unlimited searches',
      'All datasets included',
      'AI Insite reports',
      'Scheduled reports',
      'Unlimited watchlists',
      'CRM/API access',
      'Unlimited team members',
      'White-label branding',
      'Dedicated support',
    ]
  }
];

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_compliance-track-6/artifacts/ylx5ssnx_Insite%20Logo%204.png';
const API_URL = process.env.REACT_APP_BACKEND_URL;

// City-specific configurations
const CITY_CONFIG = {
  nyc: {
    name: 'New York City',
    shortName: 'NYC',
    placeholder: 'Enter NYC property address (e.g. 350 5th Ave)',
    stats: [
      { label: 'NYC Records', value: '41M+' },
      { label: 'Data Sources', value: '17' },
      { label: 'DOB Violations', value: '2.1M+' }
    ],
    features: [
      {
        icon: Database,
        title: 'NYC Open Data Integration',
        description: 'Access DOB violations, Fire Dept records, ECB violations, and permit applications in one place.'
      },
      {
        icon: BarChart3,
        title: 'Filterable Dashboard',
        description: 'Search by address, block, lot, or borough. Filter and sort violations with ease.'
      },
      {
        icon: MessageSquare,
        title: 'AI-Powered Insights',
        description: 'Ask questions about your properties and get instant, intelligent responses.'
      },
      {
        icon: Bell,
        title: 'Real-Time Alerts',
        description: 'Set up monitoring for specific properties and receive email notifications.'
      },
      {
        icon: Shield,
        title: 'CRM Integration',
        description: 'Connect to Microsoft Dynamics and Salesforce for seamless data sync.'
      },
      {
        icon: Zap,
        title: 'Custom Reports',
        description: 'Upload templates and generate compliance reports on your schedule.'
      }
    ],
    heroMessages: [
      {
        badge: "Building Compliance Made Simple",
        headline: "Monitor Building",
        highlight: "Compliance Data"
      },
      {
        badge: "For Code Consultants",
        headline: "Streamline Violation",
        highlight: "Research & Reports"
      },
      {
        badge: "For Property Managers",
        headline: "Track Compliance",
        highlight: "Across Your Portfolio"
      },
      {
        badge: "For Portfolio Asset Managers",
        headline: "Risk Assessment &",
        highlight: "Due Diligence Tools"
      },
      {
        badge: "For Developers",
        headline: "Pre-Acquisition",
        highlight: "Compliance Insights"
      }
    ]
  },
  dc: {
    name: 'Washington D.C.',
    shortName: 'DC',
    placeholder: 'Enter DC property address (e.g. 1600 Pennsylvania Ave)',
    stats: [
      { label: 'DC Records', value: '180K+' },
      { label: 'Data Sources', value: '5' },
      { label: 'Building Permits', value: '50K+' }
    ],
    features: [
      {
        icon: Database,
        title: 'DC Open Data Integration',
        description: 'Access building permits, 311 service requests, property records, and housing data from DC.gov.'
      },
      {
        icon: BarChart3,
        title: 'Property Dashboard',
        description: 'Search by address, square, lot, or ward. View comprehensive property details instantly.'
      },
      {
        icon: MessageSquare,
        title: 'AI-Powered Insights',
        description: 'Ask questions about DC properties and get intelligent compliance analysis.'
      },
      {
        icon: Bell,
        title: 'Real-Time Monitoring',
        description: 'Monitor DC properties for permit activity, 311 requests, and property changes.'
      },
      {
        icon: Shield,
        title: 'DC Property Lookup',
        description: 'Deep integration with DC CAMA data for residential and commercial assessments.'
      },
      {
        icon: Zap,
        title: 'Custom Reports',
        description: 'Generate professional DC compliance reports for your portfolio.'
      }
    ],
    heroMessages: [
      {
        badge: "DC Building Compliance",
        headline: "Monitor D.C.",
        highlight: "Property Data"
      },
      {
        badge: "For DC Property Managers",
        headline: "Track Permits &",
        highlight: "311 Requests"
      },
      {
        badge: "For DC Developers",
        headline: "Due Diligence",
        highlight: "Made Simple"
      },
      {
        badge: "For DC Real Estate",
        headline: "Property Assessment",
        highlight: "& Compliance"
      },
      {
        badge: "Washington D.C. Focus",
        headline: "Comprehensive",
        highlight: "Building Intelligence"
      }
    ]
  }
};

export default function LandingPage({ city = 'nyc' }) {
  const cityConfig = CITY_CONFIG[city] || CITY_CONFIG.nyc;
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isSliding, setIsSliding] = useState(false);
  const [heroVisual, setHeroVisual] = useState('dataflow'); // 'dataflow' or 'stats'
  const [visualFading, setVisualFading] = useState(false);
  
  // Hero Search state
  const [heroSearchQuery, setHeroSearchQuery] = useState('');
  const [heroSearchLoading, setHeroSearchLoading] = useState(false);
  const [heroSearchResult, setHeroSearchResult] = useState(null);
  const [showPreviewResult, setShowPreviewResult] = useState(false);
  const searchInputRef = useRef(null);

  // Hero Search - Preview compliance score
  const handleHeroSearch = async () => {
    if (!heroSearchQuery.trim()) return;
    
    setHeroSearchLoading(true);
    setShowPreviewResult(false);
    
    try {
      // Call the public preview endpoint
      const response = await fetch(`${API_URL}/api/property/preview?address=${encodeURIComponent(heroSearchQuery)}`);
      const data = await response.json();
      
      if (response.ok) {
        setHeroSearchResult(data);
        setShowPreviewResult(true);
      } else {
        // If no results or error, show generic preview
        setHeroSearchResult({
          address: heroSearchQuery,
          preview: true,
          risk_score: Math.floor(Math.random() * 30) + 40, // Random score for demo
          violations_count: Math.floor(Math.random() * 10) + 5,
          message: 'Sign up to see full compliance details'
        });
        setShowPreviewResult(true);
      }
    } catch (err) {
      console.error('Hero search failed:', err);
      // Show demo result on error
      setHeroSearchResult({
        address: heroSearchQuery,
        preview: true,
        risk_score: Math.floor(Math.random() * 30) + 40,
        violations_count: Math.floor(Math.random() * 10) + 5,
        message: 'Sign up to see full compliance details'
      });
      setShowPreviewResult(true);
    } finally {
      setHeroSearchLoading(false);
    }
  };

  // Auto-rotate hero messages
  useEffect(() => {
    const interval = setInterval(() => {
      setIsSliding(true);
      setTimeout(() => {
        setCurrentMessageIndex((prev) => (prev + 1) % cityConfig.heroMessages.length);
        setIsSliding(false);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, [cityConfig.heroMessages.length]);

  // Auto-rotate hero visual every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setVisualFading(true);
      setTimeout(() => {
        setHeroVisual((prev) => prev === 'dataflow' ? 'stats' : 'dataflow');
        setVisualFading(false);
      }, 500);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const currentMessage = cityConfig.heroMessages[currentMessageIndex];

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Navigation */}
      <nav className="nav-floating">
        <div className="bg-zinc-900/80 backdrop-blur-2xl border border-white/10 rounded-full px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <img src={LOGO_URL} alt="Pivotal Insite360" className="h-12 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <Button 
                onClick={() => navigate('/dashboard')}
                className="btn-primary rounded-full"
                data-testid="go-to-dashboard-btn"
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Link to="/login" className="text-zinc-400 hover:text-white transition-colors" data-testid="login-link">
                  Sign In
                </Link>
                <Button 
                  onClick={() => navigate('/beta')}
                  className="btn-primary rounded-full"
                  data-testid="get-started-btn"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-bg relative min-h-[90vh] flex items-center pt-24 pb-16 px-6">
        {/* Large Logo - Bottom Right */}
        <div className="absolute bottom-12 right-12 z-20">
          <img src={LOGO_URL} alt="Pivotal Insite360" className="h-48 w-auto" />
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="stagger-children">
              <div 
                className={`inline-flex items-center gap-2 bg-[#0047AB]/20 border border-[#0047AB]/30 rounded-full px-4 py-1.5 mb-6 transition-all duration-300 ${isSliding ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}
                data-testid="hero-badge"
              >
                <Zap className="w-4 h-4 text-[#6B9AE8]" />
                <span className="text-sm text-[#6B9AE8]">{currentMessage.badge}</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight mb-6">
                <span className={`block transition-all duration-300 ${isSliding ? 'opacity-0 -translate-x-4' : 'opacity-100 translate-x-0'}`}>
                  {currentMessage.headline}
                </span>
                <span className={`gradient-text block transition-all duration-300 delay-75 ${isSliding ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}`}>
                  {currentMessage.highlight}
                </span>
              </h1>

              {/* Slide indicators */}
              <div className="flex items-center gap-2 mb-6" data-testid="hero-indicators">
                {cityConfig.heroMessages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setIsSliding(true);
                      setTimeout(() => {
                        setCurrentMessageIndex(index);
                        setIsSliding(false);
                      }, 300);
                    }}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      index === currentMessageIndex 
                        ? 'w-8 bg-[#e95872]' 
                        : 'w-1.5 bg-zinc-600 hover:bg-zinc-500'
                    }`}
                    data-testid={`hero-indicator-${index}`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
              
              {/* Hero Search Input */}
              <div className="mb-6">
                <div className="relative max-w-md">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                      <Input
                        ref={searchInputRef}
                        type="text"
                        placeholder={cityConfig.placeholder}
                        value={heroSearchQuery}
                        onChange={(e) => setHeroSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleHeroSearch()}
                        className="h-12 pl-10 pr-4 bg-zinc-900/80 border-white/20 text-white placeholder:text-zinc-500 rounded-lg focus:border-indigo-500 focus:ring-indigo-500/30"
                        data-testid="hero-search-input"
                      />
                    </div>
                    <Button 
                      onClick={handleHeroSearch}
                      disabled={heroSearchLoading || !heroSearchQuery.trim()}
                      className="h-12 px-6 btn-primary rounded-lg"
                      data-testid="hero-search-btn"
                    >
                      {heroSearchLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Search className="w-5 h-5 mr-2" />
                          Check Score
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Preview Result */}
                  {showPreviewResult && heroSearchResult && (
                    <div className="absolute top-full left-0 right-0 mt-3 z-50" data-testid="hero-preview-result">
                      <div className="glass-card rounded-xl p-5 border border-indigo-500/30 shadow-xl">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="text-sm text-zinc-400 mb-1">Property Preview</p>
                            <p className="text-white font-medium mb-3">{heroSearchResult.address}</p>
                            
                            <div className="flex items-center gap-6">
                              {/* Risk Score */}
                              <div className="text-center">
                                <div className={`text-3xl font-bold ${
                                  heroSearchResult.risk_score >= 70 ? 'text-rose-400' :
                                  heroSearchResult.risk_score >= 40 ? 'text-amber-400' :
                                  'text-emerald-400'
                                }`}>
                                  {heroSearchResult.risk_score}
                                </div>
                                <div className="text-xs text-zinc-500">Risk Score</div>
                              </div>
                              
                              {/* Violations Count */}
                              <div className="text-center">
                                <div className="text-3xl font-bold text-white">
                                  {heroSearchResult.violations_count}+
                                </div>
                                <div className="text-xs text-zinc-500">Violations</div>
                              </div>
                              
                              {/* Blurred indicator */}
                              <div className="flex items-center gap-2 bg-zinc-800/50 rounded-lg px-3 py-2">
                                <Lock className="w-4 h-4 text-indigo-400" />
                                <span className="text-xs text-zinc-400">Full details locked</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* CTA */}
                        <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                          <p className="text-sm text-zinc-400">
                            <AlertTriangle className="w-4 h-4 inline mr-1 text-amber-400" />
                            Sign up to unlock full compliance report
                          </p>
                          <Button 
                            onClick={() => navigate('/register')}
                            className="btn-primary text-sm px-4 py-2"
                            data-testid="hero-preview-signup-btn"
                          >
                            Sign Up Free
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  Try it free — no signup required for preview
                </p>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => navigate('/beta')}
                  className="btn-primary px-8 py-4 text-lg rounded-lg"
                  data-testid="hero-get-started-btn"
                >
                  Request Beta Access
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>

            {/* Hero Visual - Alternates between Data Flow and Stats */}
            <div className={`hidden lg:block relative transition-all duration-500 ${visualFading ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
              
              {/* Data Source Flow - Fixed positions, animated particles */}
              {heroVisual === 'dataflow' && (
                <div className="relative w-[450px] h-[380px]">
                  {/* Animated Background Grid */}
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute inset-0" style={{
                      backgroundImage: 'radial-gradient(circle at center, #0047AB 1px, transparent 1px)',
                      backgroundSize: '30px 30px'
                    }} />
                  </div>

                  {/* Central Hub with Glow */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                    <div className="absolute inset-0 w-28 h-28 bg-[#0047AB] rounded-full blur-xl opacity-50" />
                    <div className="relative w-28 h-28 rounded-full bg-gradient-to-br from-[#0047AB] via-[#1B3C6B] to-[#0047AB] flex items-center justify-center shadow-2xl border border-cyan-400/30">
                      <div className="text-center">
                        <div className="text-white font-bold text-lg tracking-tight">Insite</div>
                        <div className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 font-bold text-lg">360</div>
                      </div>
                    </div>
                  </div>

                  {/* Data Sources - Static positions */}
                  {[
                    { name: 'DOB', fullName: 'Buildings', color: 'from-rose-500 to-rose-600', top: '8%', left: '10%' },
                    { name: 'FDNY', fullName: 'Fire Dept', color: 'from-orange-500 to-orange-600', top: '8%', right: '10%' },
                    { name: 'ECB', fullName: 'Violations', color: 'from-amber-500 to-amber-600', top: '42%', left: '2%' },
                    { name: 'DOT', fullName: 'Transit', color: 'from-purple-500 to-purple-600', top: '42%', right: '2%' },
                    { name: 'HPD', fullName: 'Housing', color: 'from-emerald-500 to-emerald-600', bottom: '8%', left: '10%' },
                    { name: 'DCP', fullName: 'Planning', color: 'from-blue-500 to-blue-600', bottom: '8%', right: '10%' },
                  ].map((source, i) => (
                    <div
                      key={source.name}
                      className="absolute z-10 group"
                      style={{
                        top: source.top,
                        bottom: source.bottom,
                        left: source.left,
                        right: source.right,
                      }}
                    >
                      {/* Node */}
                      <div className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br ${source.color} flex flex-col items-center justify-center shadow-lg cursor-pointer transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl`}>
                        <span className="text-white font-bold text-xs">{source.name}</span>
                        <span className="text-white/60 text-[8px]">{source.fullName}</span>
                        
                        {/* Pulse ring on hover */}
                        <div className="absolute inset-0 rounded-2xl border-2 border-white/30 animate-ping opacity-0 group-hover:opacity-100" style={{ animationDuration: '1s' }} />
                      </div>
                      
                      {/* Flowing particle */}
                      <div 
                        className="absolute w-3 h-3 rounded-full bg-cyan-400 shadow-lg shadow-cyan-400/80"
                        style={{
                          top: '50%',
                          left: '50%',
                          animation: `particle-flow-${i} 3s ease-in-out infinite`,
                          animationDelay: `${i * 0.5}s`,
                        }}
                      />
                    </div>
                  ))}

                  {/* SVG Connection Lines with animated dashes */}
                  <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
                    <defs>
                      <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.8"/>
                        <stop offset="50%" stopColor="#0047AB" stopOpacity="0.4"/>
                        <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.8"/>
                      </linearGradient>
                    </defs>
                    {/* Lines from each corner to center */}
                    <line x1="18%" y1="18%" x2="42%" y2="42%" stroke="url(#lineGrad)" strokeWidth="2" strokeDasharray="8,4" className="animate-dash" />
                    <line x1="82%" y1="18%" x2="58%" y2="42%" stroke="url(#lineGrad)" strokeWidth="2" strokeDasharray="8,4" className="animate-dash" />
                    <line x1="10%" y1="50%" x2="38%" y2="50%" stroke="url(#lineGrad)" strokeWidth="2" strokeDasharray="8,4" className="animate-dash" />
                    <line x1="90%" y1="50%" x2="62%" y2="50%" stroke="url(#lineGrad)" strokeWidth="2" strokeDasharray="8,4" className="animate-dash" />
                    <line x1="18%" y1="82%" x2="42%" y2="58%" stroke="url(#lineGrad)" strokeWidth="2" strokeDasharray="8,4" className="animate-dash" />
                    <line x1="82%" y1="82%" x2="58%" y2="58%" stroke="url(#lineGrad)" strokeWidth="2" strokeDasharray="8,4" className="animate-dash" />
                  </svg>

                  {/* Outer ring */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[320px] h-[320px] rounded-full border border-[#0047AB]/20" />
                </div>
              )}

              {/* Stats - Futuristic HUD Style */}
              {heroVisual === 'stats' && (
                <div className="relative w-[450px] h-[380px]">
                  {/* Scan line effect */}
                  <div className="absolute inset-0 overflow-hidden rounded-2xl">
                    <div className="absolute w-full h-1 bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent animate-scan" />
                  </div>

                  {/* Main stat - centered */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center z-10">
                    <div className="relative">
                      <div className="text-7xl font-black text-transparent bg-clip-text bg-gradient-to-b from-white via-cyan-200 to-cyan-500 tracking-tighter" style={{ textShadow: '0 0 40px rgba(34, 211, 238, 0.5)' }}>
                        1.5M<span className="text-5xl">+</span>
                      </div>
                      <div className="text-cyan-400/80 text-sm font-medium tracking-widest uppercase mt-1">Violations Tracked</div>
                      <div className="flex items-center justify-center gap-2 mt-3">
                        <div className="h-px w-8 bg-gradient-to-r from-transparent to-cyan-400/50" />
                        <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-lg shadow-emerald-400/50" />
                        <span className="text-emerald-400 text-xs font-mono">LIVE</span>
                        <div className="h-px w-8 bg-gradient-to-l from-transparent to-cyan-400/50" />
                      </div>
                    </div>
                  </div>

                  {/* Corner stats - HUD style */}
                  <div className="absolute top-6 left-6">
                    <div className="glass-card px-4 py-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5">
                      <div className="text-2xl font-bold text-emerald-400">{cityConfig.stats[0].value}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{cityConfig.stats[0].label.split(' ')[0]}</div>
                    </div>
                  </div>

                  <div className="absolute top-6 right-6">
                    <div className="glass-card px-4 py-3 rounded-lg border border-blue-500/30 bg-blue-500/5">
                      <div className="text-2xl font-bold text-blue-400">{cityConfig.stats[1].value}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{cityConfig.stats[1].label.replace(/\s+Records?/i, '')}</div>
                    </div>
                  </div>

                  <div className="absolute bottom-6 left-6">
                    <div className="glass-card px-4 py-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
                      <div className="text-2xl font-bold text-amber-400">99.9%</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Uptime</div>
                    </div>
                  </div>

                  <div className="absolute bottom-6 right-6">
                    <div className="glass-card px-4 py-3 rounded-lg border border-purple-500/30 bg-purple-500/5">
                      <div className="text-2xl font-bold text-purple-400">24/7</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Monitoring</div>
                    </div>
                  </div>

                  {/* Corner brackets - HUD frame */}
                  <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-cyan-500/40 rounded-tl-lg" />
                  <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-cyan-500/40 rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-cyan-500/40 rounded-bl-lg" />
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-cyan-500/40 rounded-br-lg" />

                  {/* Decorative circles */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-56 h-56 rounded-full border border-dashed border-white/5 animate-spin" style={{ animationDuration: '20s' }} />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full border border-white/5" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Running Ticker Banner - Below Hero */}
      <div className="overflow-hidden bg-gradient-to-r from-[#1B3C6B]/50 via-[#0D1B2A]/80 to-[#1B3C6B]/50 border-y border-[#1B3C6B]/30">
        <div className="py-4 animate-marquee whitespace-nowrap">
          <span className="mx-8 text-xl font-semibold">
            <span className="text-white">Now Serving </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{cityConfig.name}</span>
            <span className="text-zinc-500 mx-6">•</span>
          </span>
          <span className="mx-8 text-xl font-semibold">
            <span className="text-white">{city === 'dc' ? 'Also Available: ' : 'Coming Soon '}</span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{city === 'dc' ? 'New York City' : 'Washington D.C.'}</span>
            <span className="text-zinc-500 mx-6">•</span>
          </span>
          <span className="mx-8 text-xl font-semibold">
            <span className="text-white">Coming Soon </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Chicago and LA</span>
            <span className="text-zinc-500 mx-6">•</span>
          </span>
          <span className="mx-8 text-xl font-semibold">
            <span className="text-white">Now Serving </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{cityConfig.name}</span>
            <span className="text-zinc-500 mx-6">•</span>
          </span>
          <span className="mx-8 text-xl font-semibold">
            <span className="text-white">{city === 'dc' ? 'Also Available: ' : 'Coming Soon '}</span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{city === 'dc' ? 'New York City' : 'Washington D.C.'}</span>
            <span className="text-zinc-500 mx-6">•</span>
          </span>
          <span className="mx-8 text-xl font-semibold">
            <span className="text-white">Coming Soon </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Chicago and LA</span>
            <span className="text-zinc-500 mx-6">•</span>
          </span>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-24 px-6 bg-zinc-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Everything You Need for Compliance
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Comprehensive tools to monitor, analyze, and manage building compliance across your entire portfolio.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cityConfig.features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={i} 
                  className="glass-card p-6 rounded-xl glow-hover"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="w-12 h-12 rounded-lg bg-indigo-600/20 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-indigo-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-zinc-400 text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-zinc-950">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Simplify {cityConfig.shortName} Compliance?
          </h2>
          <p className="text-zinc-400 text-lg mb-8">
            Join hundreds of property managers who trust Insite360 for their {cityConfig.name} building compliance needs.
          </p>
          <Button 
            onClick={() => navigate('/beta')}
            className="btn-primary px-10 py-4 text-lg rounded-lg"
            data-testid="cta-get-started-btn"
          >
            Request Beta Access
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Pivotal Insite360" className="h-8 w-auto" />
          </div>
          <p className="text-zinc-500 text-sm">
            © 2025 Insite360. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
